"use client";

import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { type Address, isAddress } from "viem";

import { ImpersonatorBanner } from "./ImpersonatorBanner";

const IMPERSONATED_ADDRESS_KEY = "devtools:impersonatedAddress";

function useDevtoolsState() {
  const [isDevtoolsEnabled] = useState(true);
  const [isDevtoolsOpen, setIsDevtoolsOpen] = useState(false);
  const [isImpersonatorOpen, setIsImpersonatorOpen] = useState(false);
  const [impersonatedAddress, _setImpersonatedAddress] = useState<Address | undefined>(() => {
    if (typeof window === "undefined") return undefined;
    const stored = localStorage.getItem(IMPERSONATED_ADDRESS_KEY);
    return stored && isAddress(stored) ? stored : undefined;
  });

  const setImpersonatedAddress = useCallback((address: Address | undefined) => {
    _setImpersonatedAddress(address);
    if (address) {
      localStorage.setItem(IMPERSONATED_ADDRESS_KEY, address);
    } else {
      localStorage.removeItem(IMPERSONATED_ADDRESS_KEY);
    }
  }, []);

  const [isAppConfigOpen, setIsAppConfigOpen] = useState(false);

  const isDevtoolsInUse = Boolean(impersonatedAddress || isDevtoolsEnabled);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.shiftKey && event.ctrlKey) {
        if (event.key === "E") {
          setIsImpersonatorOpen((prev) => !prev);
        } else if (event.key === "C") {
          setIsDevtoolsOpen((prev) => !prev);
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return {
    isDevtoolsInUse,
    isDevtoolsOpen,
    setIsDevtoolsOpen,
    isImpersonatorOpen,
    setIsImpersonatorOpen,
    impersonatedAddress,
    setImpersonatedAddress,
    isAppConfigOpen,
    setIsAppConfigOpen,
  };
}

type DevtoolsContextType = ReturnType<typeof useDevtoolsState>;

const DevtoolsContext = createContext<DevtoolsContextType | undefined>(undefined);

export function useDevtools() {
  const context = useContext(DevtoolsContext);
  if (!context) throw new Error("useDevtools must be called within a DevtoolsProvider");
  return context;
}

interface DevtoolsProviderProps {
  children: ReactNode;
}

export function DevtoolsProvider(props: DevtoolsProviderProps) {
  const { children } = props;

  const state = useDevtoolsState();

  return (
    <DevtoolsContext.Provider value={state}>
      {children}
      <ImpersonatorBanner />
    </DevtoolsContext.Provider>
  );
}
